import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import YouTube from 'react-youtube';
import { API } from '../App';
import { Music, Play, Sparkles, X } from 'lucide-react';
import ChannelMenu from './channel/ChannelMenu';
import ShareButton from './channel/ShareButton';

const TVChannel = () => {
  const [videos, setVideos] = useState([]);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [player, setPlayer] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userUnmuted, setUserUnmuted] = useState(false);
  
  // Détecter iOS
  const isIOS = () => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
           (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  };
  
  // Initialiser showPlayButton à true si iOS, false sinon
  const [showPlayButton, setShowPlayButton] = useState(false);
  const [currentSchedule, setCurrentSchedule] = useState(null);
  const [bannerState, setBannerState] = useState({ visible: false, mode: null });
  const [bannerDismissed, setBannerDismissed] = useState(
    () => sessionStorage.getItem('special_banner_dismissed') === '1'
  );
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeShow, setActiveShow] = useState(null);

  // Calcule l'état du bandeau "Spécial Dimanche" en heure de Paris.
  // - 'live'   : Dimanche 10h-20h => programmation en cours
  // - 'teaser' : Vendredi (tout le jour), Samedi (tout le jour), Dimanche avant 10h
  // - null     : sinon (masqué)
  const getBannerMode = () => {
    // Override for QA via URL ?banner=teaser|live|none
    const params = new URLSearchParams(window.location.search);
    const override = params.get('banner');
    if (override === 'teaser' || override === 'live') return override;
    if (override === 'none') return null;
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Paris',
      weekday: 'short',
      hour: '2-digit',
      hour12: false,
    }).formatToParts(new Date());
    const weekday = parts.find((p) => p.type === 'weekday')?.value || '';
    const hour = parseInt(parts.find((p) => p.type === 'hour')?.value || '0', 10);
    if (weekday === 'Sun' && hour >= 10 && hour < 20) return 'live';
    if (weekday === 'Fri' || weekday === 'Sat') return 'teaser';
    if (weekday === 'Sun' && hour < 10) return 'teaser';
    return null;
  };

  // Détermine la programmation en cours en heure de Paris :
  //  - Dimanche 10h00–20h00 => special
  //  - Samedi & Dimanche (hors plage spéciale) => weekend
  //  - Lundi à Vendredi => mon/tue/wed/thu/fri (avec repli sur 'week')
  const WEEKDAY_MAP = { Mon: 'mon', Tue: 'tue', Wed: 'wed', Thu: 'thu', Fri: 'fri' };
  const getCurrentSchedule = () => {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Paris',
      weekday: 'short',
      hour: '2-digit',
      hour12: false,
    }).formatToParts(new Date());
    const weekday = parts.find((p) => p.type === 'weekday')?.value || '';
    const hour = parseInt(parts.find((p) => p.type === 'hour')?.value || '0', 10);
    const isSunday = weekday === 'Sun';
    const isSaturday = weekday === 'Sat';
    if (isSunday && hour >= 10 && hour < 20) return 'special';
    if (isSaturday || isSunday) return 'weekend';
    return WEEKDAY_MAP[weekday] || 'week';
  };

  useEffect(() => {
    const schedule = getCurrentSchedule();
    setCurrentSchedule(schedule);
    setBannerState({ visible: true, mode: getBannerMode() });
    fetchVideos(schedule);
  }, []);

  // Vérifier toutes les minutes si la programmation a changé (transition à minuit)
  useEffect(() => {
    const intervalId = setInterval(() => {
      const newSchedule = getCurrentSchedule();
      const newBannerMode = getBannerMode();
      setBannerState((prev) => (prev.mode === newBannerMode ? prev : { visible: true, mode: newBannerMode }));
      if (newSchedule !== currentSchedule) {
        console.log(`Changement de programmation: ${currentSchedule} -> ${newSchedule}`);
        setCurrentSchedule(newSchedule);
        fetchVideos(newSchedule);
        setCurrentVideoIndex(0);
      }
    }, 60 * 1000);
    return () => clearInterval(intervalId);
  }, [currentSchedule]);

  // Détection d'un show "live" : tout show dont la fenêtre de diffusion est en cours
  useEffect(() => {
    const checkLiveShow = async () => {
      try {
        const res = await axios.get(`${API}/shows`);
        const now = Date.now();
        const live = (res.data || []).find((s) => {
          const start = new Date(s.scheduled_at).getTime();
          const end = start + ((s.duration_minutes || 0) * 60 + (s.duration_seconds || 0)) * 1000;
          return !isNaN(start) && now >= start && now < end;
        });
        setActiveShow((prev) => {
          const prevId = prev?.id || null;
          const newId = live?.id || null;
          return prevId === newId ? prev : (live || null);
        });
      } catch (e) {
        console.error('Show poll error', e);
      }
    };
    checkLiveShow();
    const intervalId = setInterval(checkLiveShow, 30 * 1000);
    return () => clearInterval(intervalId);
  }, []);

  // Bascule du player quand activeShow change
  useEffect(() => {
    if (!player) return;
    try {
      if (activeShow) {
        const showId = extractVideoId(activeShow.youtube_url);
        if (showId) {
          player.loadVideoById(showId);
          if (userUnmuted) {
            setTimeout(() => { try { player.unMute(); } catch (_e) {} }, 500);
          }
        }
      } else if (videos[currentVideoIndex]) {
        const normalId = extractVideoId(videos[currentVideoIndex].youtube_url);
        if (normalId) {
          player.loadVideoById(normalId);
          if (userUnmuted) {
            setTimeout(() => { try { player.unMute(); } catch (_e) {} }, 500);
          }
        }
      }
    } catch (e) {
      console.error('Switch error:', e);
    }
  }, [activeShow, player]);

  const handleDismissBanner = () => {
    setBannerDismissed(true);
    sessionStorage.setItem('special_banner_dismissed', '1');
  };

  const WEEKDAY_SLOTS = ['mon', 'tue', 'wed', 'thu', 'fri'];

  const fetchVideos = async (schedule) => {
    try {
      setIsLoading(true);
      setError(null);
      const scheduleParam = schedule || getCurrentSchedule();
      let response = await axios.get(`${API}/videos?schedule=${scheduleParam}`);
      // Repli automatique: si le créneau spécifique au jour de semaine est vide, utilise 'week'
      if ((!response.data || response.data.length === 0) && WEEKDAY_SLOTS.includes(scheduleParam)) {
        response = await axios.get(`${API}/videos?schedule=week`);
      }

      if (response.data && response.data.length > 0) {
        setVideos(response.data);
      } else {
        setError('No videos available. Please contact admin to add videos.');
      }
    } catch (err) {
      console.error('Error fetching videos:', err);
      setError('Failed to load videos. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const extractVideoId = (url) => {
    try {
      const patterns = [
        /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/,
        /youtube\.com\/embed\/([^&\n?#]+)/,
        /youtube\.com\/v\/([^&\n?#]+)/
      ];
      
      for (const pattern of patterns) {
        const match = url.match(pattern);
        if (match) return match[1];
      }
      
      return url;
    } catch (e) {
      console.error('Error extracting video ID:', e);
      return url;
    }
  };

  const playNextVideo = useCallback(() => {
    if (videos.length === 0) return;
    
    const nextIndex = (currentVideoIndex + 1) % videos.length;
    setCurrentVideoIndex(nextIndex);
    
    // Utiliser loadVideoById pour garder le même player
    if (player && videos[nextIndex]) {
      const nextVideoId = extractVideoId(videos[nextIndex].youtube_url);
      player.loadVideoById(nextVideoId);
      
      // Réactiver le son si nécessaire
      if (userUnmuted) {
        setTimeout(() => {
          try {
            player.unMute();
          } catch (e) {
            console.error('Erreur son:', e);
          }
        }, 500);
      }
    }
  }, [videos, currentVideoIndex, player, userUnmuted]);

  const onPlayerReady = (event) => {
    setPlayer(event.target);
    
    // Démarrer la première vidéo automatiquement
    setTimeout(() => {
      try {
        event.target.playVideo();
      } catch (e) {
        console.error('Erreur onReady:', e);
      }
    }, 100);
  };

  const handlePlayVideo = () => {
    setShowPlayButton(false);
    if (player) {
      try {
        player.playVideo();
        
        // Réactiver le son si nécessaire
        if (userUnmuted) {
          player.unMute();
        }
      } catch (e) {
        console.error('Erreur play:', e);
      }
    }
  };

  const onPlayerStateChange = (event) => {
    // Détecter quand l'utilisateur active manuellement le son
    if (event.data === 1) { // Video is playing
      try {
        const isMuted = event.target.isMuted();
        if (!isMuted && !userUnmuted) {
          setUserUnmuted(true);
          console.log('Utilisateur a activé le son - mémorisé');
        }
        // Forcer le unmute si l'utilisateur l'avait activé
        if (userUnmuted && isMuted) {
          event.target.unMute();
          console.log('Son forcé car utilisateur l\'avait activé');
        }
      } catch (e) {
        console.error('Error checking mute status:', e);
      }
    }
  };

  const onPlayerEnd = () => {
    // Si c'est la première vidéo (jingle) qui se termine, activer automatiquement le son
    if (currentVideoIndex === 0 && !userUnmuted && player) {
      try {
        player.unMute();
        setUserUnmuted(true);
      } catch (e) {
        console.error('Erreur activation auto du son:', e);
      }
    }
    // Si un show est en cours, ne pas avancer dans la queue normale.
    // On reste sur le show jusqu'à expiration (le poll basculera vers la queue automatiquement).
    if (activeShow) {
      try {
        player?.playVideo();
      } catch (_e) {}
      return;
    }
    playNextVideo();
  };

  const onPlayerError = (event) => {
    console.error('YouTube player error:', event);
    if (activeShow) return;
    setTimeout(() => {
      playNextVideo();
    }, 2000);
  };

  const opts = {
    height: '100%',
    width: '100%',
    playerVars: {
      autoplay: 1,
      mute: 1,
      controls: 1,
      modestbranding: 1,
      rel: 0,
      fs: 1,
      playsinline: 1,
    },
  };

  if (isLoading) {
    return (
      <div className="loading-screen" data-testid="loading-screen">
        <div className="loading-content">
          <Music className="loading-icon" size={64} />
          <h2>Grace TV Gospel</h2>
          <p>Loading blessed content...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-screen" data-testid="error-screen">
        <div className="error-content">
          <img 
            src="https://customer-assets.emergentagent.com/job_bcc12bb2-d1c3-4c30-aab1-6d93b6ded326/artifacts/auol33u9_logo_4x.png" 
            alt="Grace TV Gospel Logo" 
            className="error-logo"
          />
          <h2>Grace TV Gospel</h2>
          <p className="error-message">{error}</p>
        </div>
      </div>
    );
  }

  const currentVideo = videos[currentVideoIndex];
  const videoId = currentVideo ? extractVideoId(currentVideo.youtube_url) : null;

  return (
    <div className="tv-channel" data-testid="tv-channel">
      <div className="tv-header">
        <button
          type="button"
          onClick={() => setMenuOpen(true)}
          className="tv-logo-button"
          aria-label="Ouvrir le menu de la chaîne"
          data-testid="open-menu-button"
        >
          <img 
            src="https://customer-assets.emergentagent.com/job_bcc12bb2-d1c3-4c30-aab1-6d93b6ded326/artifacts/auol33u9_logo_4x.png" 
            alt="Grace TV Gospel Logo" 
            className="tv-logo"
            data-testid="channel-logo"
          />
        </button>
      </div>

      <ChannelMenu open={menuOpen} onClose={() => setMenuOpen(false)} />
      <ShareButton />

      {bannerState.mode && !bannerDismissed && (
        <div
          className={`special-banner special-banner-${bannerState.mode}`}
          data-testid={`special-banner-${bannerState.mode}`}
          role="status"
        >
          {bannerState.mode === 'live' ? (
            <>
              <span className="special-banner-pulse" aria-hidden="true" />
              <span className="special-banner-label">EN DIRECT</span>
              <span className="special-banner-text">
                Programmation Spéciale Dimanche · jusqu'à 20h00
              </span>
            </>
          ) : (
            <>
              <Sparkles size={16} className="special-banner-icon" aria-hidden="true" />
              <span className="special-banner-label">Rendez-vous spécial</span>
              <span className="special-banner-text">
                Dimanche · 10h00–20h00 (heure de Paris)
              </span>
            </>
          )}
          <button
            type="button"
            className="special-banner-close"
            onClick={handleDismissBanner}
            aria-label="Fermer le bandeau"
            data-testid="special-banner-close"
          >
            <X size={14} />
          </button>
        </div>
      )}
      
      <div className="video-container" data-testid="video-container">
        {videos.length > 0 && videoId && (
          <>
            <YouTube
              videoId={videoId}
              key="grace-tv-player"
              opts={opts}
              onReady={onPlayerReady}
              onStateChange={onPlayerStateChange}
              onEnd={onPlayerEnd}
              onError={onPlayerError}
              className="youtube-player"
            />
          </>
        )}
      </div>

      <div className="video-info" data-testid="video-info">
        <div className="now-playing">
          <Music size={20} />
          <span>{activeShow ? 'Émission spéciale en cours' : 'Now Playing'}</span>
        </div>
        <h3 className="video-title">{activeShow ? activeShow.title : (currentVideo?.title || 'Loading...')}</h3>
        <div className="video-counter">
          {activeShow ? 'Show TV' : `Video ${currentVideoIndex + 1} of ${videos.length}`}
        </div>
      </div>

      <div className="playlist-preview" data-testid="playlist-preview">
        <h4>Coming Up Next</h4>
        <div className="next-videos">
          {currentVideoIndex + 1 < videos.length ? (
            <div className="next-video-item" data-testid="next-video-0">
              <Music size={16} />
              <span>{videos[currentVideoIndex + 1]?.title}</span>
            </div>
          ) : videos.length > 1 && (
            <div className="next-video-item loop-indicator" data-testid="loop-indicator">
              <Music size={16} />
              <span>{videos[0]?.title} (Loop)</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TVChannel;